import {
  DOCS_PLACEHOLDER_TOKEN_RE,
  type DocsModelCapability,
  type DocsPlaceholder,
  parseDocsPlaceholder,
} from '@nadoutong/contracts';

/**
 * Runtime values for the five static placeholders. The document itself
 * is environment-neutral and brand-neutral because of these, not
 * because the strings happen to look generic.
 */
export interface DocsStaticPlaceholderValues {
  readonly gatewayOrigin: string;
  readonly openaiBase: string;
  readonly consoleOrigin: string;
  readonly apiKeyPlaceholder: string;
  readonly brandName: string;
}

export interface DocsModelValues {
  readonly one: Readonly<Partial<Record<DocsModelCapability, string>>>;
  readonly many: Readonly<Partial<Record<DocsModelCapability, string>>>;
}

export interface DocsPlaceholderValues {
  readonly static: DocsStaticPlaceholderValues;
  readonly models: DocsModelValues;
}

/**
 * Substitute every `{{…}}` token the contract recognises. An unknown
 * token, an unknown model capability, or a known token we have no value
 * for is left on screen as `{{token}}` — a visible finding, not a blank.
 */
export function substituteDocsPlaceholders(text: string, values: DocsPlaceholderValues): string {
  return text.replace(DOCS_PLACEHOLDER_TOKEN_RE, (token) => {
    const parsed = parseDocsPlaceholder(token);
    if (parsed === null) return token;
    const resolved = resolveParsedPlaceholder(parsed, values);
    return resolved === null ? token : resolved;
  });
}

function resolveParsedPlaceholder(
  parsed: DocsPlaceholder,
  values: DocsPlaceholderValues,
): string | null {
  if (parsed.form === 'static') {
    switch (parsed.token) {
      case '{{gateway_origin}}':
        return values.static.gatewayOrigin;
      case '{{openai_base}}':
        return values.static.openaiBase;
      case '{{console_origin}}':
        return values.static.consoleOrigin;
      case '{{api_key_placeholder}}':
        return values.static.apiKeyPlaceholder;
      case '{{brand_name}}':
        return values.static.brandName;
      default:
        return null;
    }
  }
  if (parsed.form === 'model') {
    const value = values.models.one[parsed.capability];
    return value === undefined ? null : value;
  }
  const value = values.models.many[parsed.capability];
  return value === undefined ? null : value;
}
